/**
 * Line-buffered NDJSON parser.
 *
 * Reads a fetch Response body stream and invokes `onLine` with each parsed JSON
 * object as its newline-delimited line arrives, so callers can render
 * incrementally instead of waiting for the whole body.
 *
 * @param {ReadableStream<Uint8Array>} body    response.body
 * @param {(value: any) => void}        onLine  called once per complete line
 * @returns {Promise<void>}                     resolves when the stream ends
 */
export async function readNdjsonStream(body, onLine) {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      buffer = flushLines(buffer, onLine)
    }

    // Trailing bytes + any final line with no newline terminator.
    buffer += decoder.decode()
    flushLines(buffer + '\n', onLine)
  } finally {
    reader.releaseLock()
  }
}

/**
 * Emits every complete line in `buffer`, returns the unterminated remainder.
 *
 * @param {string} buffer
 * @param {(value: any) => void} onLine
 * @returns {string} the trailing partial line
 */
function flushLines(buffer, onLine) {
  let newlineIndex = buffer.indexOf('\n')

  while (newlineIndex !== -1) {
    const line = buffer.slice(0, newlineIndex).trim()
    buffer = buffer.slice(newlineIndex + 1)

    if (line.length > 0) {
      onLine(JSON.parse(line))
    }

    newlineIndex = buffer.indexOf('\n')
  }

  return buffer
}
